import type { JSX } from "react";
import { Link, Text, View } from "@/components";
import type { LandingCta } from "@/types/elearning/landing";
import cn from "@/utils/cn";

export interface LandingHeroProps {
  title: string;
  /** Párrafos de presentación, en orden. */
  description: string[];
  /** Llamados a la acción (Ingresar / Registrate). Son navegación: se renderizan como Link. */
  ctas?: LandingCta[];
}

/**
 * Amarillo sobre navy (7.5:1) y blanco sobre navy: pares del manual de marca.
 * El secundario lleva el borde dorado en oscuro, como en la propuesta del
 * cliente (pagina 4 del PDF); en claro el modelo lo deja claro y se mantiene.
 * La etiqueta sigue en blanco en los dos temas.
 */
const ctaStyles: Record<LandingCta["variant"], string> = {
  primary:
    "bg-(--sv-yellow) text-(--sv-navy) rounded-full px-8 py-3 font-semibold hover:opacity-90 transition-opacity",
  secondary:
    "border border-white/70 dark:border-(--sv-yellow) text-white rounded-full px-8 py-3 font-medium hover:bg-white/10 transition-colors"
};

/**
 * @name LandingHero
 * @description Encabezado de la landing pública (pestaña Inicio): panel azul
 * de marca (`--sv-panel`: navy sobre fondo claro, azul intermedio en oscuro,
 * donde el fondo ya es navy y el panel tiene que despegarse) con el título en Lora precedido por la barra vertical
 * amarilla (motivo del wordmark "| SEGURIDAD VIAL"), párrafos de presentación
 * y llamados a la acción hacia el ingreso y el registro. Server Component sin
 * estado: el copy llega por props desde `app/(pages)/(landing)/content.ts`.
 *
 * @param {LandingHeroProps} props - Título, párrafos y CTAs.
 * @returns {JSX.Element} El hero renderizado.
 */
const LandingHero = ({
  title,
  description,
  ctas = []
}: LandingHeroProps): JSX.Element => (
  <View
    type="section"
    className="flex flex-col gap-6 rounded-3xl bg-(--sv-panel) px-6 py-10 sm:px-10 lg:px-14 lg:py-16"
  >
    <Text
      type="h1"
      className="border-l-4 border-(--sv-yellow) pl-4 text-3xl lg:text-5xl font-bold text-white text-balance"
    >
      {title}
    </Text>
    <View className="flex flex-col gap-4 max-w-3xl">
      {description.map((paragraph) => (
        <Text
          key={paragraph}
          type="p"
          className="text-base lg:text-lg text-white/85 leading-relaxed"
        >
          {paragraph}
        </Text>
      ))}
    </View>
    {ctas.length > 0 && (
      <View className="flex flex-wrap gap-4 pt-2">
        {ctas.map((cta) => (
          <Link
            key={cta.href}
            href={cta.href}
            className={cn("text-sm text-center", ctaStyles[cta.variant])}
          >
            {cta.label}
          </Link>
        ))}
      </View>
    )}
  </View>
);

export default LandingHero;
